import type { ButtonProps } from '@chakra-ui/react';
import { Button } from '@chakra-ui/react';
import { useStripe } from '@stripe/react-stripe-js';
import type { StripeError } from '@stripe/stripe-js';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster'; // Import toaster for errors
import { captureException } from '@/libs/sentry';
import { usePayoutCheckoutData } from '@/stores/payout-checkout-store'; // Import store

type ProcessCheckoutPaymentButtonProps = ButtonProps;

export const getStripePaymentErrorMessage = (message?: string | null) =>
  message?.trim() || 'An unexpected error occurred during payment.';

/**
 * Stripe refuses to confirm a PaymentIntent that is already `canceled` with
 * `payment_intent_unexpected_state`, the code it uses for any operation the
 * intent's state forbids. The code alone is therefore not enough: the status
 * Stripe returns with the error is what says the payout was closed (expired, or
 * cancelled by the brand or by Stripe) and can no longer be paid.
 */
export const isCanceledPaymentIntentError = (error: StripeError): boolean =>
  error.code === 'payment_intent_unexpected_state' && error.payment_intent?.status === 'canceled';

export const ProcessCheckoutPaymentButton: React.FC<ProcessCheckoutPaymentButtonProps> = ({
  ...props
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const {
    payout,
    paymentIntentClientSecret: clientSecret,
    selectedPaymentMethodId: paymentMethod,
  } = usePayoutCheckoutData();

  const [isProcessing, setIsProcessing] = useState(false);

  const canSubmit = paymentMethod && !!stripe && !!clientSecret;

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!canSubmit || isProcessing) {
      return;
    }

    setIsProcessing(true);

    if (!stripe || !clientSecret || !paymentMethod) {
      setIsProcessing(false);
      return;
    }

    const returnUrl = `${window.location.origin}/payout/${payout?.id}/checkout/processing`;

    const { error } = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        return_url: returnUrl,
        payment_method: paymentMethod,
      },
      redirect: 'if_required',
    });

    // Check for special case: requires_capture status
    const isRequiresCapture =
      error?.code === 'payment_intent_unexpected_state' &&
      error?.payment_intent?.status === 'requires_capture';

    if (error && !isRequiresCapture) {
      const errorMessage = isCanceledPaymentIntentError(error)
        ? t('payouts.checkout.actions.paymentIntentCanceled')
        : getStripePaymentErrorMessage(error.message);
      captureStripePaymentConfirmationError(error, payout?.id);
      toaster.create({
        title: t('payouts.checkout.actions.paymentFailed'),
        description: errorMessage,
        type: 'error',
      });
    } else {
      // Either successful payment or requires_capture state
      console.log('Payment confirmed or requires capture - proceeding to next step');
      // Navigate to processing page on success
      navigate(`/payout/${payout?.id}/checkout/processing`);
    }

    setIsProcessing(false);
  };

  return (
    <>
      <Button
        colorPalette="primary"
        onClick={handleSubmit}
        loading={isProcessing}
        disabled={!canSubmit || isProcessing}
        width="100%"
        {...props}
      >
        {isProcessing
          ? t('payouts.checkout.actions.processing')
          : t('payouts.checkout.actions.confirmAndPay')}
      </Button>
    </>
  );
};

export function captureStripePaymentConfirmationError(error: StripeError, payoutId?: string) {
  // Card refusals are handled by the checkout UI; API and integration failures
  // still need an application exception for investigation.
  if (error.type === 'card_error') return;

  const message = error.message || error.code || error.type || 'unknown Stripe error';
  const sentryError = new Error(`Stripe payment confirmation error: ${message}`);

  sentryError.name = 'StripePaymentConfirmationError';

  captureException(sentryError, {
    stripeError: {
      type: error.type,
      code: error.code,
      paymentIntentStatus: error.payment_intent?.status,
    },
    payoutId,
  });
}
